import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PrinterIcon, ArrowLeftIcon } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Romaneio de Expedição — página imprimível.
 * URL: /romaneio/expedicao/:placa
 * Ctrl+P ou botão Imprimir gera PDF via impressora do navegador.
 */
export default function RomaneioExpedicao() {
  const { placa } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/romaneio/expedicao/${encodeURIComponent(placa)}`);
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [placa]);

  const now = new Date();

  if (error) return <div className="min-h-screen bg-white text-black flex items-center justify-center">
    <div className="text-center">
      <div className="text-red-600 font-bold text-lg mb-2">Erro ao carregar romaneio</div>
      <div className="text-sm text-gray-600">{error}</div>
    </div>
  </div>;
  if (!data) return <div className="min-h-screen bg-white text-black flex items-center justify-center">Carregando romaneio...</div>;

  return (
    <div className="bg-white text-black min-h-screen">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 1cm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @media screen {
          body { background: #f0f0f0; }
        }
      `}</style>

      <div className="no-print bg-[#0A0A0C] px-6 py-3 flex items-center justify-between text-white sticky top-0 z-10">
        <Link to="/expedicao" className="text-xs uppercase tracking-widest text-[#00E5FF] hover:underline flex items-center gap-1">
          <ArrowLeftIcon className="w-3 h-3" /> Voltar
        </Link>
        <button onClick={() => window.print()} data-testid="romaneio-print" className="bg-[#FFD60A] text-black font-bold uppercase tracking-wider text-xs px-6 py-2 hover:bg-[#FFD60A]/85 flex items-center gap-2">
          <PrinterIcon className="w-4 h-4" /> Imprimir / PDF
        </button>
      </div>

      <div className="max-w-[210mm] mx-auto bg-white p-8 shadow-lg my-4" data-testid="romaneio-container">
        {/* Header */}
        <div className="flex items-start justify-between border-b-4 border-black pb-4 mb-6">
          <div>
            <div className="text-xs tracking-widest text-gray-600">CHEM·WMS · Envase Químico</div>
            <div className="text-3xl font-bold mt-1">ROMANEIO DE EXPEDIÇÃO</div>
            <div className="text-xs text-gray-600 mt-1">Documento fiscal complementar · Emissão: {now.toLocaleString("pt-BR")}</div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black text-black">#{placa}</div>
            <div className="text-[10px] uppercase tracking-widest text-gray-600 mt-1">Placa do veículo</div>
          </div>
        </div>

        {/* Bloco identificação */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Motorista</div>
            <div className="font-bold text-lg">{data.motorista || "—"}</div>
            <div className="text-xs mt-1">CPF: {data.cpf_motorista || "—"}</div>
          </div>
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Destinatário</div>
            <div className="font-bold text-lg">{data.cliente || "—"}</div>
            <div className="text-xs mt-1">Pedido: {data.no_pedido || "—"} · NF: {data.nf_produto || "—"}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Data de saída</div>
            <div className="font-mono font-bold">{data.data_saida ? new Date(data.data_saida).toLocaleString("pt-BR") : "—"}</div>
          </div>
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Total de contentores</div>
            <div className="font-mono font-bold text-2xl">{data.total_contentores}</div>
          </div>
          <div className="border border-black p-3">
            <div className="text-[9px] uppercase tracking-wider text-gray-600">Volume total (L)</div>
            <div className="font-mono font-bold text-2xl">{Number(data.total_litros).toLocaleString("pt-BR")}</div>
          </div>
        </div>

        {/* Tabela contentores */}
        <table className="w-full border-collapse text-xs mb-6">
          <thead>
            <tr className="bg-black text-white">
              <th className="border border-black px-2 py-1 text-left">#</th>
              <th className="border border-black px-2 py-1 text-left">N° Série</th>
              <th className="border border-black px-2 py-1 text-left">Tipo</th>
              <th className="border border-black px-2 py-1 text-left">Produto</th>
              <th className="border border-black px-2 py-1 text-left">Lote</th>
              <th className="border border-black px-2 py-1 text-right">Qtd (L)</th>
              <th className="border border-black px-2 py-1 text-left">Lacres</th>
              <th className="border border-black px-2 py-1 text-left">GPS</th>
            </tr>
          </thead>
          <tbody>
            {data.contentores.map((c, i) => (
              <tr key={c.no_serie}>
                <td className="border border-black px-2 py-1 font-mono">{i + 1}</td>
                <td className="border border-black px-2 py-1 font-mono font-bold">{c.no_serie}</td>
                <td className="border border-black px-2 py-1">{c.tipo_armazenamento} {c.capacidade}L</td>
                <td className="border border-black px-2 py-1">{c.nome_produto}</td>
                <td className="border border-black px-2 py-1 font-mono">{c.no_lote}</td>
                <td className="border border-black px-2 py-1 text-right font-mono">{c.qtd_envasado}</td>
                <td className="border border-black px-2 py-1 font-mono text-[10px]">{(c.lacres || []).join(" · ")}</td>
                <td className="border border-black px-2 py-1 font-mono text-[10px]">{c.gps_id}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Assinaturas */}
        <div className="grid grid-cols-2 gap-8 mt-16">
          <div className="text-center">
            <div className="border-t-2 border-black pt-1 text-xs">Assinatura do Motorista</div>
            <div className="text-[10px] text-gray-600 mt-1">CPF: {data.cpf_motorista || "___________"}</div>
          </div>
          <div className="text-center">
            <div className="border-t-2 border-black pt-1 text-xs">Recebido por</div>
            <div className="text-[10px] text-gray-600 mt-1">Nome / RG / Data</div>
          </div>
        </div>

        <div className="text-[9px] text-center text-gray-500 mt-12 border-t border-gray-300 pt-2">
          Documento gerado eletronicamente pelo CHEM·WMS · {now.toISOString()}
        </div>
      </div>
    </div>
  );
}
